/** Facetten-Filterleiste (Fach/Klasse/Tag/Lehrer/Lektion) fuer Chat-Scope (DD5/DD7). */
import type { ChatFacets } from '@/types/teacher-chat'
import type { ActiveScopeFilter } from './scopeUtils'
import { isFilterEmpty, formatDayShort } from './scopeUtils'

interface ChatFacetBarProps {
  facets: ChatFacets
  active: ActiveScopeFilter
  onChange: (next: ActiveScopeFilter) => void
}

interface DimensionConfig {
  key: keyof ActiveScopeFilter
  label: string
  entries: { value: string; label: string }[]
}

export function ChatFacetBar({ facets, active, onChange }: ChatFacetBarProps) {
  const dimensions: DimensionConfig[] = [
    {
      key: 'subjects' as const,
      label: 'Fach',
      entries: facets.subjects.map(s => ({ value: s.name, label: s.name })),
    },
    {
      key: 'classes' as const,
      label: 'Klasse',
      entries: facets.classes.map(c => ({ value: c.name, label: c.name })),
    },
    {
      key: 'days' as const,
      label: 'Tag',
      entries: facets.days.map(d => ({ value: d.name, label: formatDayShort(d.name) })),
    },
    {
      key: 'teachers' as const,
      label: 'Lehrperson',
      entries: facets.teachers.map(t => ({ value: t.teacher_id, label: t.teacher_name || '?' })),
    },
  ].filter(d => d.entries.length > 0)

  if (dimensions.length === 0) return null

  const toggle = (dim: keyof ActiveScopeFilter, value: string) => {
    const next: ActiveScopeFilter = {
      subjects: new Set(active.subjects),
      classes: new Set(active.classes),
      days: new Set(active.days),
      teachers: new Set(active.teachers),
    }
    const set = next[dim] as Set<string>
    if (set.has(value)) set.delete(value)
    else set.add(value)
    onChange(next)
  }

  return (
    <div className="px-4 md:px-6 py-2 border-b border-gray-100 bg-gray-50/50 shrink-0">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() =>
            onChange({
              subjects: new Set(),
              classes: new Set(),
              days: new Set(),
              teachers: new Set(),
            })
          }
          disabled={isFilterEmpty(active)}
          className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
            isFilterEmpty(active)
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Alle
        </button>
        {dimensions.map(dim => (
          <div key={dim.key} className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
              {dim.label}:
            </span>
            {dim.entries.map(e => {
              const isActive = (active[dim.key] as Set<string>).has(e.value)
              return (
                <button
                  key={e.value}
                  onClick={() => toggle(dim.key, e.value)}
                  className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                    isActive
                      ? 'bg-blue-500 text-white font-medium'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {e.label}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
